import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from 'react-query'; // Added useQueryClient
import { toast } from 'react-toastify';
import {
  delete_stock_in,
  get_all_filters_item_stock_in, // This will be our main data fetching function
  // get_all_stock_in, // Likely replaced by get_all_filters_item_stock_in
} from '../api/stocksApi';
import Flatpickr from 'react-flatpickr';
import { get_all_vendors } from '../../vendors/api/vendorsApi';
import { permissionCheck } from '../../../helper-functions/PermissionCheck';
import { formatDateFlatpickr, formatDateWithoutTime, formatDateWithTime } from '../../../helper-functions/DateFunction';
import { KTIcon } from '../../../../_metronic/helpers';

// --- Type Definitions ---
// Define a specific type for Stock In items based on your API response
type StockInItem = {
  id: number;
  vendor_name?: string | null; // Vendor name might be null if not associated
  date_time: string; // Date of stock in
  has_po: number; // 0 or 1
  updated_at: string;
  updated_by_name?: string | null;
  stock_type?: string; // If this field exists
  // Add any other fields specific to a stock_in record
};

interface VendorType { // For filter dropdown
  id: number;
  supplier_name: string | undefined;
};

type ApiPaginationInfo = {
  total: number;
  per_page: number;
  current_page: number;
  last_page: number;
  from: number;
  to: number;
};

type ApiStockInDataPayload = {
  stocks: StockInItem[]; // Assuming API returns 'stocks' array
  pagination: ApiPaginationInfo;
  links: { /* ... link structure ... */ };
};

type ApiStockInFullResponse = {
  data: { // From Axios
    success: boolean;
    message: string;
    data: ApiStockInDataPayload; // From your API structure
  };
};
// --- End Type Definitions ---


var stock_in_add: boolean, stock_in_edit: boolean, stock_in_view: boolean, stock_in_logs: boolean, stock_in_delete: boolean;

function StockInList() {
  const [stockList, setStockList] = useState<StockInItem[]>([]); // For current page

  // Filter States
  const [selectedVendor, setSelectedVendor] = useState<string>('');
  const [fromDateFilter, setFromDateFilter] = useState<string>('');
  const [toDateFilter, setToDateFilter] = useState<string>('');
  const [searchInput, setSearchInput] = useState<string>('');

  // State for API filter payload
  const [filterPayloadForApi, setFilterPayloadForApi] = useState<any>({
    fromDateFilter: '',
    toDateFilter: '',
    selectedVendor: '',
    // searchTerm: '', // If backend supports search
  });

  // Pagination State
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [itemsPerPage, setItemsPerPage] = useState<number>(10); // Renamed
  const [totalPages, setTotalPages] = useState<number>(0);
  const [totalItems, setTotalItems] = useState<number>(0);
  const [currentRangeFrom, setCurrentRangeFrom] = useState<number>(1);
  const [currentRangeTo, setCurrentRangeTo] = useState<number>(0);

  // const [sortBy, setSortBy] = useState<{ key: string, ascending: boolean } | null>(null); // For server-side sorting

  const [selectedStock, setSelectedStock] = useState<StockInItem | null>(null);
  const [showAllVendor, setShowAllVendor] = useState<VendorType[]>([]);

  const fromDateRef = useRef<Flatpickr>(null);
  const toDateRef = useRef<Flatpickr>(null);
  // const [delayedLoading, setDelayedLoading] = useState(true); // Handled by react-query

  const navigate = useNavigate();
  const queryClient = useQueryClient();

  stock_in_add = permissionCheck('stock-create'); // Assuming similar permission prefix
  stock_in_edit = permissionCheck('stock-update');
  stock_in_view = permissionCheck('stock-view');
  stock_in_logs = permissionCheck('stock-activity-log');
  stock_in_delete = permissionCheck('stock-delete');

  // Fetch Vendors for filter dropdown
  const { data: AllVendors } = useQuery('allVendorsForStockInFilter', get_all_vendors, {
    refetchOnWindowFocus: false,
    onSuccess: (response) => {
      const vendors = response?.data?.data || response?.data;
      if (vendors && Array.isArray(vendors)) {
        setShowAllVendor(vendors as VendorType[]);
      }
    }
  });


  // --- Main Query for Filtered and Paginated Stock In Records ---
  const {
    // data: filteredStockInQueryData, // Full API response
    isLoading,
    isFetching,
    isError,
    error,
  } = useQuery<ApiStockInFullResponse, Error>(
    ['filtered_stock_in', filterPayloadForApi, currentPage, itemsPerPage],
    () => get_all_filters_item_stock_in(
      filterPayloadForApi.fromDateFilter,
      filterPayloadForApi.toDateFilter,
      filterPayloadForApi.selectedVendor,
      currentPage,
      itemsPerPage
      // filterPayloadForApi.searchTerm, // If server-side search
    ),
    {
      keepPreviousData: true,
      refetchOnWindowFocus: false,
      onSuccess: (response) => {
        const apiPayload = response.data.data;

        if (apiPayload && Array.isArray(apiPayload.stocks)) {
          setStockList(apiPayload.stocks);

          if (apiPayload.pagination) {
            setTotalItems(apiPayload.pagination.total);
            setTotalPages(apiPayload.pagination.last_page);
            setCurrentPage(apiPayload.pagination.current_page);
            setCurrentRangeFrom(apiPayload.pagination.from);
            setCurrentRangeTo(apiPayload.pagination.to);
          } else {
            // If pagination is not present, handle gracefully
            setTotalItems(apiPayload.stocks.length || 0);
            setTotalPages(1);
            setCurrentPage(1);
            setCurrentRangeFrom(1);
            setCurrentRangeTo(apiPayload.stocks.length || 0);
          }
        } else {
          // Fallback if stocks is not an array
          setStockList([]);
          setTotalItems(0);
          setTotalPages(0);
          setCurrentRangeFrom(1);
          setCurrentRangeTo(0);
        }
      },
      onError: (error: any) => {
        toast.error(error.message || "Failed to fetch stock in records.", { autoClose: 3000 });
        setStockList([]);
        setTotalItems(0);
        setTotalPages(0);
      },
    }
  );

  // Client-side search (optional)
  const displayedStockList = searchInput
    ? stockList.filter((stock) =>
      stock.stock_type?.toLowerCase().includes(searchInput.toLowerCase()) ||
      stock.vendor_name?.toLowerCase().includes(searchInput.toLowerCase()) ||
      stock.date_time?.toLowerCase().includes(searchInput.toLowerCase()) // Assuming date_time is string here
    )
    : stockList;

  // Mutation for deleting stock in record
  const handleDeleteStock = useMutation(
    (id: number) => delete_stock_in(id),
    {
      onSuccess: (response) => {
        toast.success(response.message || "Stock In record deleted successfully.", { autoClose: 3000 });
        queryClient.invalidateQueries(['filtered_stock_in', filterPayloadForApi, currentPage, itemsPerPage]);
      },
      onError: (error: any) => {
        const errorMessage = error.response?.data?.message || error.message || "Failed to delete Stock In record.";
        toast.error(errorMessage, { autoClose: 3000 });
      },
    }
  );


  const handleApplyFilters = () => {
    setCurrentPage(1);
    setFilterPayloadForApi({
      fromDateFilter: fromDateFilter,
      toDateFilter: toDateFilter,
      selectedVendor: selectedVendor,
      // searchTerm: searchInput, // If server-side search
    });
  };

  const handleClearFilters = () => {
    if (fromDateRef.current) fromDateRef.current.flatpickr.clear();
    if (toDateRef.current) toDateRef.current.flatpickr.clear();
    setFromDateFilter('');
    setToDateFilter('');
    setSelectedVendor('');
    setSearchInput('');

    setCurrentPage(1);
    setFilterPayloadForApi({
      fromDateFilter: '', toDateFilter: '', selectedVendor: '', /* searchTerm: '' */
    });
  };

  // Date picker handlers
  const handleDateFromChange = (selectedDates: Date[]) => {
    if (selectedDates && selectedDates[0]) {
      const formatted = formatDateFlatpickr(selectedDates[0]);
      setFromDateFilter(formatted);
      if (toDateRef.current) toDateRef.current.flatpickr.set('minDate', selectedDates[0]);
    } else {
      setFromDateFilter('');
      if (toDateRef.current) toDateRef.current.flatpickr.set('minDate', null);
    }
  };
  const handleDateToChange = (selectedDates: Date[]) => {
    if (selectedDates && selectedDates[0]) {
      const formatted = formatDateFlatpickr(selectedDates[0]);
      setToDateFilter(formatted);
    } else {
      setToDateFilter('');
    }
  };

  // Pagination handlers
  const itemsPerPageOptions = [10, 20, 30, 50, 100]; // Renamed from itemsPerPage to avoid conflict
  const handleNextPage = () => {
    if (currentPage < totalPages) setCurrentPage(currentPage + 1);
  };
  const handlePreviousPage = () => {
    if (currentPage > 1) setCurrentPage(currentPage - 1);
  };
  const handleItemsPerPageChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setItemsPerPage(Number(e.target.value));
    setCurrentPage(1);
  };

  // Navigation handlers
  const handleViewStockIn = (stock: StockInItem) => navigate("/stocks/view-stock-in", { state: { id: stock.id } });
  const handleStockInEdit = (stock: StockInItem) => navigate("/stocks/edit-stock-in", { state: { id: stock.id } }); // Pass ID or full object as needed
  const handleStockInLogs = (stock: StockInItem) => navigate("/stocks/stock-in-logs", { state: { id: stock.id } });

  // Removed client-side sorting. Server-side sorting needed.

  if (isLoading && !isFetching && currentPage === 1) {
    return <div className='d-flex justify-content-center align-items-center' style={{ height: '200px' }}><div className='spinner-border text-primary' role='status'><span className='visually-hidden'>Loading...</span></div></div>;
  }

  return (
    <div className='card card-xxl-stretch mx-4 mb-5 mb-xl-10'>
      {/* Card Header */}
      <div className='card-header border-0 pt-5'>
        <h3 className='card-title align-items-start flex-column'>
          <span className='card-label fw-bolder fs-3 mb-1'>Stock In List</span>
          <span className='text-muted mt-1 fw-semibold fs-7'>
            {totalItems > 0 ? `${totalItems} records found` : 'No records'}
          </span>
        </h3>
        <div className='card-toolbar'>
          {stock_in_add && (
            <button className='btn btn-sm btn-light-dark' onClick={() => navigate('/stocks/add-stock-in')}>
              <KTIcon iconName='plus' className='fs-3' /> Add Stock In
            </button>
          )}
        </div>
      </div>

      {/* Filters Section */}
      <div className='card-body pt-0'>
        <div className='row mb-4 align-items-center gy-3'>
          <div className='col-md-3'>
            <div className='input-group'>
              <span className="input-group-text"><KTIcon iconName='magnifier' className='fs-3' /></span>
              <input
                type='text'
                className='form-control form-control-solid'
                placeholder='Search vendor, date...'
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
              />
            </div>
          </div>
          <div className='col-md-3'>
            <Flatpickr
              ref={fromDateRef}
              onChange={handleDateFromChange}
              className='form-control form-control-solid'
              placeholder='From Date'
              options={{ dateFormat: 'd/m/Y' }}
            />
          </div>
          <div className='col-md-2'>
            <Flatpickr
              ref={toDateRef}
              onChange={handleDateToChange}
              className='form-control form-control-solid'
              placeholder='To Date'
              options={{ dateFormat: 'd/m/Y' }}
            />
          </div>
          <div className='col-md-2'>
            <select
              className='form-select form-select-solid'
              value={selectedVendor}
              onChange={(e) => setSelectedVendor(e.target.value)}
            >
              <option value=''>Vendor...</option>
              {showAllVendor.map((vendor) => (
                <option key={vendor.id} value={vendor.id}>{vendor.supplier_name}</option>
              ))}
            </select>
          </div>
          <div className='col-md-2 d-flex justify-content-end align-items-center'>
            <button className='btn btn-light-danger me-2' onClick={handleClearFilters} disabled={isFetching} style={{ whiteSpace: 'nowrap' }}>
              <KTIcon iconName='cross' className='fs-5 me-1' /> Clear
            </button>
            <button className='btn btn-primary' onClick={handleApplyFilters} disabled={isFetching} style={{ whiteSpace: 'nowrap' }}>
              <KTIcon iconName='filter-search' className='fs-5 me-1' /> Apply
            </button>
          </div>
        </div>
      </div>

      {/* Table Section */}
      <div className='card-body py-3'>
        {isFetching && !isLoading && <div className='text-center text-muted p-3'><i>Updating stock list...</i></div>}
        <div className='table-responsive'>
          <table className='table table-row-dashed table-row-gray-300 align-middle gs-0 gy-4'>
            <thead>
              <tr className='fw-bold text-muted bg-light'>
                <th className='ps-4 min-w-150px rounded-start'>Vendor Name</th>
                <th className='min-w-120px'>Date</th>
                <th className='min-w-100px'>Has PO</th>
                <th className='min-w-150px'>Updated By</th>
                <th className='min-w-150px'>Updated At</th>
                {(stock_in_delete || stock_in_edit || stock_in_logs || stock_in_view) &&
                  <th className='min-w-150px text-center rounded-end'>Actions</th>
                }
              </tr>
            </thead>
            <tbody>
              {isLoading && currentPage === 1 ? (
                <tr><td colSpan={6} className="text-center py-5"><div className='spinner-border text-primary' /></td></tr>
              ) : isError ? (
                <tr><td colSpan={6} className="text-center py-5 text-danger">Error: {error?.message}</td></tr>
              ) : displayedStockList.length > 0 ? (
                displayedStockList.map((stock) => (
                  <tr key={stock.id}>
                    <td className='ps-4'>
                      <span className='text-dark fw-bold text-hover-primary d-block fs-6 cursor-pointer' onClick={() => handleViewStockIn(stock)}>
                        {stock.vendor_name || "N/A"}
                      </span>
                    </td>
                    <td><span className='text-dark fw-semibold d-block fs-6'>{formatDateWithoutTime(stock.date_time)}</span></td>
                    <td>
                      <span className={`badge badge-lg ${stock.has_po === 1 ? 'badge-light-success' : 'badge-light-primary'} fs-7 fw-bold`}>
                        {stock.has_po === 1 ? 'Yes' : 'No'}
                      </span>
                    </td>
                    <td><span className='text-dark fw-semibold d-block fs-6'>{stock.updated_by_name || 'N/A'}</span></td>
                    <td><span className='text-dark fw-semibold d-block fs-6'>{formatDateWithTime(stock.updated_at)}</span></td>
                    {(stock_in_delete || stock_in_edit || stock_in_logs || stock_in_view) &&
                      <td className='text-center'>
                        <div className='d-flex justify-content-center flex-shrink-0'>
                          {stock_in_view && (<button onClick={() => handleViewStockIn(stock)} className='btn btn-icon btn-bg-light btn-active-color-primary btn-sm me-1' title="View"><KTIcon iconName='eye' className='fs-3' /></button>)}
                          {/* Edit button conditional logic: only if has_po is 0 */}
                          {stock.has_po === 0 && stock_in_edit && (<button onClick={() => handleStockInEdit(stock)} className='btn btn-icon btn-bg-light btn-active-color-primary btn-sm me-1' title="Edit"><KTIcon iconName='pencil' className='fs-3' /></button>)}
                          {stock_in_logs && (<button onClick={() => handleStockInLogs(stock)} className='btn btn-icon btn-bg-light btn-active-color-primary btn-sm me-1' title="Logs"><KTIcon iconName='notepad-bookmark' className='fs-3' /></button>)}
                          {stock_in_delete && (<button onClick={() => setSelectedStock(stock)} className='btn btn-icon btn-bg-light btn-active-color-danger btn-sm' title="Delete" data-bs-toggle='modal' data-bs-target='#kt_modal_delete_stock_in'><KTIcon iconName='trash' className='fs-3' /></button>)}
                        </div>
                      </td>
                    }
                  </tr>
                ))
              ) : (
                !isFetching && <tr><td colSpan={6} className="text-center py-5">No stock in records found.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Footer: Pagination and Items Per Page */}
      {totalItems > 0 && (
        <div className='card-footer d-flex justify-content-between align-items-center py-4'>
          <div>
            <span className='text-muted me-2'>Showing:</span>
            <select
              className='form-select form-select-sm d-inline-block w-auto me-3'
              value={itemsPerPage}
              onChange={handleItemsPerPageChange}
              disabled={isFetching}
            >
              {itemsPerPageOptions.map(option => (<option key={option} value={option}>{option}</option>))}
            </select>
            <span className='text-muted'>
              {currentRangeFrom} - {currentRangeTo > totalItems ? totalItems : currentRangeTo} of {totalItems} records
            </span>
          </div>

          <nav>
            <ul className="pagination pagination-sm mb-0">
              <li className={`page-item ${currentPage === 1 || isFetching ? 'disabled' : ''}`}>
                <button className="page-link" onClick={handlePreviousPage} disabled={currentPage === 1 || isFetching}>Previous</button>
              </li>
              {/* Optional: Page Number Logic */}
              <li className={`page-item ${currentPage === totalPages || totalPages === 0 || isFetching ? 'disabled' : ''}`}>
                <button className="page-link" onClick={handleNextPage} disabled={currentPage === totalPages || totalPages === 0 || isFetching}>Next</button>
              </li>
            </ul>
          </nav>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      <div className="modal fade" tabIndex={-1} id="kt_modal_delete_stock_in">
        <div className="modal-dialog">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title">Delete Stock In Confirmation</h5>
              <button type="button" className="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
            </div>
            <div className="modal-body">
              <p>Are you sure you want to delete this stock in record? <br />
                Associated with vendor: <strong>{selectedStock?.vendor_name || "N/A"}</strong>,
                dated: <strong>{selectedStock ? formatDateWithoutTime(selectedStock.date_time) : ''}</strong>.
              </p>
              <p className='text-muted small'>This action cannot be undone.</p>
            </div>
            <div className="modal-footer">
              <button type="button" className="btn btn-light" data-bs-dismiss="modal">Cancel</button>
              <button
                type="button"
                className="btn btn-danger"
                onClick={() => { if (selectedStock) handleDeleteStock.mutate(selectedStock.id); }}
                data-bs-dismiss="modal"
                disabled={handleDeleteStock.isLoading}
              >
                {handleDeleteStock.isLoading ? 'Deleting...' : 'Delete Stock In'}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default StockInList;